import {
    getPrimaryAnalysis,
} from "@mrflark/spl_sdk";
import type {
    MediaItem,
} from "@mrflark/spl_service_model";
import {
    Modal,
    Skeleton,
} from "@mrflark/ui";
import {
    useQuery,
} from "@tanstack/react-query";
import type {
    ReactElement,
} from "preact/compat";
import {
    splClient,
} from "../../api/client.ts";

type SimilarItemsModalProps = {
    isOpen: boolean;
    onClose: () => void;
    item: MediaItem | null;
    onNavigateToItem: (item: MediaItem) => void;
};

export const SimilarItemsModal = ({
    isOpen,
    onClose,
    item,
    onNavigateToItem,
}: SimilarItemsModalProps): ReactElement => {
    const hasAiData = !!item?.ai_analyses &&
        Object.keys(item.ai_analyses).length > 0;

    const {
        data,
        isLoading,
    } = useQuery({
        queryKey: ["similarMediaModal", item?.id],
        queryFn: () =>
            splClient.getSimilar(item?.id ?? "", {
                limit: 30,
            }),
        enabled: isOpen && hasAiData && !!item?.id,
        staleTime: 5 * 60 * 1000,
    });

    const handleItemClick = (clickedItem: MediaItem): void => {
        onNavigateToItem(clickedItem);
        onClose();
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title="Similar Items"
            size="lg"
        >
            {isLoading
                ? (
                    <div className="grid grid-cols-4 gap-3">
                        {Array.from({
                            length: 12,
                        }).map((_, index) => (
                            <Skeleton
                                key={index}
                                className="aspect-square rounded-lg"
                            />
                        ))}
                    </div>
                )
                : !data || data.items.length === 0
                ? (
                    <p className="text-text-secondary text-center py-8">
                        No similar items found
                    </p>
                )
                : (
                    <div className="grid grid-cols-4 gap-3 max-h-[70vh] overflow-y-auto">
                        {data.items.map((similarItem) => {
                            const similarity =
                                (similarItem as Record<string, unknown>)
                                    .similarity as number | undefined;
                            const description = getPrimaryAnalysis(
                                similarItem.ai_analyses,
                            )?.entry.description;
                            return (
                                <button
                                    type="button"
                                    key={similarItem.id}
                                    onClick={() => handleItemClick(similarItem)}
                                    className="group relative aspect-square rounded-lg overflow-hidden border border-border hover:border-accent transition-colors"
                                    title={description || similarItem.id}
                                >
                                    {similarItem.media_type === "video"
                                        ? (
                                            <video
                                                src={similarItem
                                                    .thumbnail_url ||
                                                    similarItem.media_url}
                                                className="w-full h-full object-cover"
                                                muted
                                            />
                                        )
                                        : (
                                            <img
                                                src={similarItem
                                                    .thumbnail_url ||
                                                    similarItem.media_url}
                                                alt=""
                                                className="w-full h-full object-cover"
                                                loading="lazy"
                                            />
                                        )}
                                    {similarity !== undefined && (
                                        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                            <span className="text-white text-sm font-medium">
                                                {Math.round(similarity * 100)}%
                                            </span>
                                        </div>
                                    )}
                                </button>
                            );
                        })}
                    </div>
                )}
        </Modal>
    );
};
